import { useRef, useState, useMemo } from "react";
import { Card, Flex, Row, Col, Skeleton, InputRef } from "antd";
import { AppstoreOutlined, CrownOutlined, FieldTimeOutlined } from "@ant-design/icons";
import MetricCard from "components/MetricCard.component";
import { getPackageAnalyticsOverview } from "hooks/queries/analytics";
import { translate, translateWithVars } from "shared/languages";
import { colors } from "shared/theme/color";
import { tnum } from "shared/components/TableText";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import { getYtdDayRange } from "shared/utils/dayjs";
import {
  AnalyticsRange,
  CustomDayRange,
  PackageAnalyticsRow,
  PackageType,
} from "shared/types";
import RangeSegmented from "./RangeSegmented";
import CustomRangePicker from "./CustomRangePicker";
import ReportPageHeading from "./ReportPageHeading";
import ExportMenu from "./ExportMenu.component";
import { useAuth } from "context/AuthContextProvider";
import { useTableSort } from "hooks/useTableSort";
import { analyticsRangeStamp } from "shared/utils/download";
import { MAX_PDF_ROWS } from "pdf/layout";
import { loadReportPdf } from "shared/utils/loadPdfChunk";
import { ReportColumn, reportColumn } from "shared/reports/reportColumns";
import { ReportTable } from "shared/reports/ReportTable";
import {
  formatCountOrDash,
  formatDaysOrDash,
  formatPercentOrDash,
} from "shared/reports/columnCatalogue";
import { produkOverviewSpec } from "pdf/reports/specs/archetype1";
import { useReportPageCount } from "./useReportPageCount";
import { ProductNameCell } from "./ProductNameCell";
import { textPx } from "shared/constants/zoom";

// A null health metric means "no qualifying cohort yet" (e.g. nobody's
// purchase of a brand-new product has expired), not zero: the shared
// formatters render "-" so that read stays distinct from "0%".

interface PackageOverviewContentProps {
  type: PackageType;
  title: string;
  backTo: string;
}

// Shared "performance overview" report — used for both Paket and Membership,
// which are otherwise structurally identical reports scoped to a fixed type
// (no in-page toggle; the type is decided by which route you're on).
//
// Regular AND promo products (`promo: "all"` below; Andrew, 2026-10-06). It
// was regular-only for a while, which left the Aktif card short of every
// client holding a promo package and gave promo products no performance view
// anywhere (Pendapatan dari Paket (Promo) covers their revenue only). Each
// promo row carries its "Promo · …" chip, and its Tingkat Pembelian Ulang is a
// dash: a promo that has ended cannot be bought again, so the rate would only
// ever read as churn. The Excel export lists the same rows.
const PackageOverviewContent = ({
  type,
  title,
  backTo,
}: PackageOverviewContentProps) => {
  const { state: authState } = useAuth();
  const { sortState, onSortChange } = useTableSort();
  // Defaults to YTD — customRange starts pre-populated to match so the first
  // render already queries {from,to} instead of a never-sent range=ytd token.
  const ytdRange = getYtdDayRange();
  const [range, setRange] = useState<AnalyticsRange>("ytd");
  const [customRange, setCustomRange] = useState<CustomDayRange>(ytdRange);
  const isYtdActive = customRange?.from === ytdRange.from && customRange?.to === ytdRange.to;
  const searchInput = useRef<InputRef>(null);
  // includeHealthMetrics=true — this report is the one place the extra
  // repurchase-rate/utilization/pacing queries actually run (see the hook's
  // own comment); every other consumer of this endpoint omits it.
  // defaultSort="active" — this report's own on-screen default (most active
  // first); Pendapatan's reguler view passes "sales" instead (its own
  // default is revenue, not activity).
  const { data: overview, isLoading } = getPackageAnalyticsOverview(
    range,
    type,
    "all",
    customRange,
    true,
    false,
    "active",
  );

  // Paket and Membership are otherwise identical reports, but a few labels
  // literally say "Paket" — those need to come from the matching namespace
  // instead of always reading from "package".
  const namespace = type === "membership" ? "membership" : "package";

  // Columns come from the shared report catalogue (one name, width and
  // alignment per metric); this page keeps its own order, search and sorters.
  const columns: ReportColumn<PackageAnalyticsRow>[] = [
    reportColumn<PackageAnalyticsRow>(type === "membership" ? "membership" : "package", {
      dataIndex: "name",
      render: (_: string, row) => <ProductNameCell row={row} />,
      search: getColumnSearchProps<PackageAnalyticsRow, "name">({
        config: "name",
        searchInput,
      }),
    }),
    reportColumn<PackageAnalyticsRow>("active", {
      dataIndex: "active",
      // Counts PACKAGES. Once a package can serve several clients the basis
      // stops being obvious from the label alone.
      info: () => translate(`analyticsPage.${namespace}.col_active_info`),
      sorter: (a, b) => a.active - b.active,
      // Mirrors the server default (most active first).
      defaultSortOrder: "descend",
    }),
    reportColumn<PackageAnalyticsRow>("sold", {
      // No Upgrade column here, deliberately: this report shows no money
      // beside Terjual, so there is no gap to explain, and it is already
      // at the width limit. The tooltip carries the rule instead.
      dataIndex: "sold",
      info: () => translate(`analyticsPage.${namespace}.col_sold_info`),
      sorter: (a, b) => a.sold - b.sold,
    }),
    // Membership is unlimited by definition — credits_remaining is always
    // null there, so the column would read "Tidak Terbatas" on every row.
    // Meaningful for Paket only.
    ...(type === "membership"
      ? []
      : [
          reportColumn<PackageAnalyticsRow>("remaining_credits", {
            dataIndex: "credits_remaining",
            render: (value: number | null) => (
              <span style={tnum}>
                {value === null ? translate("analyticsPage.package.unlimited") : value}
              </span>
            ),
            sorter: (a, b) => (a.credits_remaining ?? 0) - (b.credits_remaining ?? 0),
          }),
        ]),
    reportColumn<PackageAnalyticsRow>("repurchase_rate", {
      dataIndex: "repurchase_rate",
      // The rule is genuinely non-obvious now (same product any size,
      // early renewals count, shared packages, one purchase one copy),
      // so it is spelled out rather than guessed at.
      info: () => translate(`analyticsPage.${namespace}.col_repurchase_rate_info`),
      render: (value: number | null) => <span style={tnum}>{formatPercentOrDash(value)}</span>,
      sorter: (a, b) => (a.repurchase_rate ?? -1) - (b.repurchase_rate ?? -1),
    }),
    // Meaningful for Paket only — Membership has no finite credit pool to
    // measure exhaustion pace against.
    ...(type === "membership"
      ? []
      : [
          reportColumn<PackageAnalyticsRow>("avg_days_to_exhaust", {
            dataIndex: "avg_days_to_exhaust",
            // Two figures rather than one blended average, because a shared copy
            // burns the same credits in a fraction of the calendar time. The
            // second line only appears once shared copies have actually run out,
            // so nothing changes for a studio that never enables sharing.
            // Same bracket form as Avg. Visits. The unit stays on the main
            // figure only, so "53 days (shared 26)" rather than repeating it.
            render: (value: number | null, row: PackageAnalyticsRow) => (
              <span style={tnum}>
                {formatDaysOrDash(value)}
                {row.avg_days_to_exhaust_shared != null && (
                  <span style={{ color: colors.ink3 }}>
                    {" "}
                    ({translate("analyticsPage.package.shared_prefix")}{" "}
                    {Math.round(row.avg_days_to_exhaust_shared)})
                  </span>
                )}
              </span>
            ),
            sorter: (a, b) => (a.avg_days_to_exhaust ?? -1) - (b.avg_days_to_exhaust ?? -1),
          }),
        ]),
    // Membership's stand-in for Paket's credit-usage columns above — since
    // unlimited plans have no credit pool, visit frequency is the only
    // usage signal available.
    ...(type === "membership"
      ? [
          reportColumn<PackageAnalyticsRow>("avg_visits", {
            dataIndex: "avg_visits_per_period",
            // Two figures rather than one blended average, matching Avg. Time to
            // Exhaust on the Paket side: several people on one membership collect
            // a multiple of the visits over the same period. The second line only
            // appears once a membership has actually been shared.
            // "4.5 (shared 19.0)" on one line rather than a second line: 18
            // stacked rows read as clutter, and the bracket keeps the row
            // height fixed. The word "shared" sits INSIDE the bracket rather
            // than only in the tooltip, because a bare "(19.0)" reads as a
            // negative number by accounting convention, and the PDF export uses
            // the same string where no tooltip exists to explain it.
            render: (value: number | null, row: PackageAnalyticsRow) => (
              <span style={tnum}>
                {formatCountOrDash(value)}
                {row.avg_visits_per_period_shared != null && (
                  <span style={{ color: colors.ink3 }}>
                    {" "}
                    ({translate("analyticsPage.package.shared_prefix")}{" "}
                    {formatCountOrDash(row.avg_visits_per_period_shared)})
                  </span>
                )}
              </span>
            ),
            sorter: (a, b) => (a.avg_visits_per_period ?? -1) - (b.avg_visits_per_period ?? -1),
          }),
        ]
      : []),
    reportColumn<PackageAnalyticsRow>("expiring_soon", {
      dataIndex: "expiring_soon",
      // A count, but its header carries the window, so it needs a little
      // more room than a plain count column to stay on two lines.
      share: 12,
      min: 120,
      // The window rides the header ("Akan Kedaluwarsa (14 Hari)"), so the
      // label is composed per render from the loaded threshold.
      label: () =>
        `${translate("analyticsPage.columns.expiring_soon")} (${overview?.expiring_soon_days ?? 14} ${translate(
          "analyticsPage.package.expiring_soon_days_unit",
        )})`,
      info: () =>
        translateWithVars(`analyticsPage.${namespace}.col_expiring_soon_info`, {
          days: overview?.expiring_soon_days ?? 14,
        }),
      sorter: (a, b) => a.expiring_soon - b.expiring_soon,
    }),
    reportColumn<PackageAnalyticsRow>("last_purchase", {
      dataIndex: "last_purchase",
      sorter: (a, b) => (a.last_purchase ?? "").localeCompare(b.last_purchase ?? ""),
    }),
  ];

  const pdfSpec = useMemo(() => produkOverviewSpec(type), [type]);
  const pageCount = useReportPageCount(pdfSpec, overview, sortState);

  return (
    <>
      <ReportPageHeading
        title={title}
        backTo={backTo}
        extra={
          <ExportMenu
            reportSlug={`performa-${type}`}
            params={
              customRange ? { from_date: customRange.from, to_date: customRange.to } : { range }
            }
            pdf={
              overview && {
                rowChoice: {
                  pagesFor: pageCount.pagesFor,
                  prepare: pageCount.prepare,
                  available: Math.min(overview.table.length, MAX_PDF_ROWS),
                  total: overview.table.length,
                },
                fileName: `spott-performa-${type}-${analyticsRangeStamp(range, customRange)}.pdf`,
                render: async (maxRows) => {
                  const { renderReportPdf } = await loadReportPdf();
                  return renderReportPdf({
                    slug: type === "membership" ? "performa-membership" : "performa-paket",
                    studio: authState.business?.name ?? "",
                    data: overview,
                    sortState,
                    maxRows,
                  });
                },
              }
            }
          />
        }
      />
      <Flex vertical gap={20}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <span style={{ fontSize: textPx(13), color: colors.ink2 }}>
            {overview?.range_label}
          </span>
          <Flex align="center" gap={10} wrap="wrap">
            <CustomRangePicker value={isYtdActive ? null : customRange} onChange={setCustomRange} />
            <RangeSegmented
              value={isYtdActive ? "ytd" : range}
              active={!customRange || isYtdActive}
              onChange={(value) => {
                setCustomRange(value === "ytd" ? ytdRange : null);
                setRange(value);
              }}
            />
          </Flex>
        </Flex>
        <Row gutter={[16, 16]}>
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Col key={i} xs={24} sm={12} lg={8}>
                <Card style={{ borderRadius: 16 }}>
                  <Skeleton active paragraph={{ rows: 2 }} />
                </Card>
              </Col>
            ))
          ) : (
            <>
              <Col xs={24} sm={12} lg={8}>
                <MetricCard
                  icon={<AppstoreOutlined />}
                  title={translate(`analyticsPage.${namespace}.kpi_active`)}
                  value={overview?.active_packages.value ?? 0}
                  information={translate(`analyticsPage.${namespace}.kpi_active_info`)}
                />
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <MetricCard
                  icon={<FieldTimeOutlined />}
                  title={translate(`analyticsPage.${namespace}.kpi_expired`)}
                  value={overview?.expired_packages.value ?? 0}
                  information={translate(
                    namespace === "membership"
                      ? "analyticsPage.package.kpi_expired_info_membership"
                      : "analyticsPage.package.kpi_expired_info",
                  )}
                />
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <MetricCard
                  icon={<CrownOutlined />}
                  // The count rides in the caption, not behind the info icon:
                  // that icon means "what does this figure mean" everywhere
                  // else, and a number is not an explanation.
                  title={
                    overview?.best_seller.count
                      ? `${translate(`analyticsPage.${namespace}.kpi_best_seller`)} · ${overview.best_seller.count} ${translate(
                          "analyticsPage.package.kpi_best_seller_unit",
                        )}`
                      : translate(`analyticsPage.${namespace}.kpi_best_seller`)
                  }
                  value={overview?.best_seller.name ?? "-"}
                  text
                />
              </Col>
            </>
          )}
        </Row>
        <Card styles={{ body: { padding: 18 } }}>
          <ReportTable<PackageAnalyticsRow>
            rowKey="package_id"
            loading={isLoading}
            columns={columns}
            dataSource={overview?.table ?? []}
            // One report component, two routes (Paket and Membership), each
            // with its own page-memory slot.
            pageMemory={`analyticsOverview.${type}`}
            onSortChange={onSortChange}
          />
        </Card>
      </Flex>
    </>
  );
};

export default PackageOverviewContent;
